import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";

export default function Landing() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#050505] relative overflow-hidden flex flex-col justify-between">
      {/* Background Gradients & Grid */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#0f0f12_1px,transparent_1px),linear-gradient(to_bottom,#0f0f12_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]"></div>
      
      <div className="absolute top-[-10%] left-[10%] w-[500px] h-[500px] bg-gradient-to-br from-[#FF6B3D]/20 to-indigo-600/10 rounded-full blur-[120px] opacity-70 pointer-events-none"></div>
      <div className="absolute bottom-[-10%] right-[10%] w-[500px] h-[500px] bg-gradient-to-tr from-indigo-900/20 to-[#FF6B3D]/10 rounded-full blur-[120px] opacity-70 pointer-events-none"></div>

      {/* Top Navbar */}
      <header className="relative z-10 w-full max-w-7xl mx-auto px-6 py-6 flex items-center justify-between border-b border-white/5 backdrop-blur-md bg-[#050505]/40">
        <div className="flex items-center gap-3">
          <span className="bg-[#FF6B3D] text-black font-['Plus_Jakarta_Sans'] font-black text-2xl px-3 py-1 rounded-lg shadow-[0_0_20px_rgba(255,107,61,0.3)]">
            byeC
          </span>
          <span className="text-xs font-['JetBrains_Mono'] text-gray-500 uppercase tracking-widest hidden sm:inline-block border-l border-white/10 pl-3">
            Startup Evaluator v2.0
          </span>
        </div>
        <div className="flex items-center gap-4">
          <a
            href="https://twitter.com/isawsukul"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs font-['JetBrains_Mono'] text-gray-400 hover:text-white transition-colors"
          >
            @isawsukul
          </a>
        </div>
      </header>

      {/* Main Hero Section */}
      <main className="relative z-10 flex-1 flex flex-col items-center justify-center px-6 py-16">
        <div className="max-w-4xl mx-auto text-center space-y-10">
          
          {/* Hero Titles */}
          <div className="space-y-4">
            <h1 className="font-['Plus_Jakarta_Sans'] font-extrabold text-5xl md:text-7xl leading-[1.1] text-white tracking-tight">
              Should You <span className="bg-gradient-to-r from-[#FF6B3D] via-[#ff885e] to-indigo-400 bg-clip-text text-transparent">Startup?</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-400 font-normal max-w-2xl mx-auto leading-relaxed">
              A sharp, brutally honest readiness evaluator that scores your startup preparedness (0–100) and gives you a reality check in under 2 minutes.
            </p>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
            <Button 
              size="lg"
              className="w-full sm:w-auto h-14 px-10 text-base font-semibold bg-[#FF6B3D] hover:bg-[#FF5522] text-black transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] shadow-[0_4px_30px_rgba(255,107,61,0.25)] hover:shadow-[0_4px_30px_rgba(255,107,61,0.4)]"
              onClick={() => navigate("/questionnaire")}
              data-testid="start-evaluation-button"
            >
              Start Free Evaluation
              <ArrowRight className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" />
            </Button>
          </div>

          {/* Stats Ticker */}
          <div className="pt-8 border-t border-white/5 max-w-md mx-auto">
            <div className="flex items-center justify-center gap-8 text-center">
              <div>
                <p className="text-3xl font-['JetBrains_Mono'] font-bold text-white">500k+</p>
                <p className="text-xs text-gray-500 uppercase tracking-wider mt-1">Copes Generated</p>
              </div>
              <div className="h-8 w-px bg-white/10"></div>
              <div>
                <p className="text-3xl font-['JetBrains_Mono'] font-bold text-white">12</p>
                <p className="text-xs text-gray-500 uppercase tracking-wider mt-1">Critical Dimensions</p>
              </div>
              <div className="h-8 w-px bg-white/10"></div>
              <div>
                <p className="text-3xl font-['JetBrains_Mono'] font-bold text-[#FF6B3D]">100%</p>
                <p className="text-xs text-gray-500 uppercase tracking-wider mt-1">Unfiltered Truth</p>
              </div>
            </div>
          </div>

        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 w-full border-t border-white/5 py-8 text-center text-xs text-gray-500 backdrop-blur-md bg-[#050505]/20">
        <p className="mb-2">No login required • Takes under 2 minutes • 100% free</p>
        <p>
          Made by Ishan Sukul aka{" "}
          <a
            href="https://twitter.com/isawsukul"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#FF6B3D] hover:underline font-medium"
          >
            @isawsukul
          </a>
        </p>
      </footer>
    </div>
  );
}
